import { PLAN_NAMES } from "../constants/planNames";
import { decodeMetadataToken } from "./token.utils";

export const formatDate = (timestamp?: number) => {
    if (!timestamp) return '-- --';
    const date = new Date(timestamp * 1000);
    return date.toLocaleDateString('en-US', {
        month: 'long',
        day: 'numeric',
        year: 'numeric'
    });
};

/**
 * Comprehensive email validation function
 * @param email - The email string to validate
 * @returns Object with validation result and optional error message
 */
export const validateEmail = (email: string): { isValid: boolean; error?: string } => {
    const trimmedEmail = email.trim();
    
    // Check if empty
    if (!trimmedEmail) {
        return { isValid: false, error: 'Email is required' };
    }
    
    // Check length (RFC 5321 limit)
    if (trimmedEmail.length > 254) {
        return { isValid: false, error: 'Email is too long' };
    }
    
    // Check for leading/trailing spaces
    if (email !== trimmedEmail) {
        return { isValid: false, error: 'Email cannot contain leading or trailing spaces' };
    }
    
    // Check for spaces in the middle
    if (trimmedEmail.includes(' ')) {
        return { isValid: false, error: 'Email cannot contain spaces' };
    }
    
    // Basic format check with comprehensive regex
    const emailRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
    if (!emailRegex.test(trimmedEmail)) {
        return { isValid: false, error: 'Please enter a valid email address' };
    }
    
    // Check for consecutive dots
    if (trimmedEmail.includes('..')) {
        return { isValid: false, error: 'Email cannot contain consecutive dots' };
    }
    
    // Check for dots at start/end of local part
    const parts = trimmedEmail.split('@');
    if (parts.length !== 2) {
        return { isValid: false, error: 'Invalid email format' };
    }
    
    const [localPart, domain] = parts;
    if (localPart.startsWith('.') || localPart.endsWith('.')) {
        return { isValid: false, error: 'Email local part cannot start or end with a dot' };
    }
    
    // Check domain structure
    if (domain.startsWith('.') || domain.endsWith('.') || domain.includes('..')) {
        return { isValid: false, error: 'Invalid domain format' };
    }
    
    return { isValid: true };
};


export const formatPlanName = (priceId: string) => {
    if(priceId.includes(PLAN_NAMES.ALL_IN_ONE)){
        return PLAN_NAMES.ALL_IN_ONE_DISPLAY;
    }
    if(priceId.includes(PLAN_NAMES.AUTOMATED_GOLD)){
        return PLAN_NAMES.AUTOMATED_GOLD_DISPLAY;
    }
    if(priceId.includes(PLAN_NAMES.LIVE_WEBINARS)){
        return PLAN_NAMES.LIVE_WEBINARS_DISPLAY;
    }
    if(priceId.includes(PLAN_NAMES.FREE_TRIAL)){
        return PLAN_NAMES.FREE_TRIAL_DISPLAY;
    }
    return priceId
      .split('-')
      .filter(part => !['USD', 'Monthly', 'Yearly'].includes(part))
      .join(' ')
      .replace(/([A-Z])/g, ' $1')
      .trim()
      .split(' ')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
      .join(' ');
  };

/**
 * Generates a strong password with customizable options
 * @param options - Configuration options for password generation
 * @returns A strong password string
 */
export const generateStrongPassword = (options: {
  length?: number;
  includeUppercase?: boolean;
  includeLowercase?: boolean;
  includeNumbers?: boolean;
  includeSymbols?: boolean;
  excludeSimilar?: boolean;
} = {}): string => {
  const {
    length = 16,
    includeUppercase = true,
    includeLowercase = true,
    includeNumbers = true,
    includeSymbols = true,
    excludeSimilar = true
  } = options;

  // Character sets
  const lowercase = 'abcdefghijklmnopqrstuvwxyz';
  const uppercase = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ';
  const numbers = '0123456789';
  const symbols = '!@#$?';
  
  // Characters to exclude if excludeSimilar is true
  const similarChars = 'il1Lo0O';
  
  let charset = '';
  let requiredChars = '';
  
  // Build character set based on options
  if (includeLowercase) {
    const chars = excludeSimilar ? lowercase.replace(/[il]/g, '') : lowercase;
    charset += chars;
    requiredChars += chars[Math.floor(Math.random() * chars.length)];
  }
  
  if (includeUppercase) {
    const chars = excludeSimilar ? uppercase.replace(/[LO]/g, '') : uppercase;
    charset += chars;
    requiredChars += chars[Math.floor(Math.random() * chars.length)];
  }
  
  if (includeNumbers) {
    const chars = excludeSimilar ? numbers.replace(/[10]/g, '') : numbers;
    charset += chars;
    requiredChars += chars[Math.floor(Math.random() * chars.length)];
  }
  
  if (includeSymbols) {
    charset += symbols;
    requiredChars += symbols[Math.floor(Math.random() * symbols.length)];
  }
  
  // Ensure we have at least one character from each required set
  let password = requiredChars;
  
  // Fill the rest of the password with random characters
  for (let i = requiredChars.length; i < length; i++) {
    password += charset[Math.floor(Math.random() * charset.length)];
  }
  
  // Shuffle the password to avoid predictable patterns
  return password.split('').sort(() => Math.random() - 0.5).join('');
};

/**
 * Generates a secure password that meets common password requirements
 * - At least 12 characters long
 * - Contains uppercase, lowercase, numbers, and symbols
 * - Excludes similar-looking characters
 * @returns A secure password string
 */
export const generateSecurePassword = (): string => {
  return generateStrongPassword({
    length: 16,
    includeUppercase: true,
    includeLowercase: true,
    includeNumbers: true,
    includeSymbols: true,
    excludeSimilar: true
  });
};

/**
 * Security utility to validate user permissions for company access
 * @param requiredRole - The minimum role required (e.g., 'OWNER', 'ADMIN', 'USER')
 * @param targetCompanyId - Optional company ID to validate access to
 * @returns Object with validation result and error message if invalid
 */
export const validateUserPermissions = (
  requiredRole: string = 'OWNER',
  targetCompanyId?: string
): { isValid: boolean; error?: string; companyId?: string } => {
  try {
    const metadata = decodeMetadataToken();
    
    // Check if user has any company access
    if (!metadata?.companies || metadata.companies.length === 0) {
      return { isValid: false, error: 'No company access found' };
    }
    
    const userCompany = metadata.companies[0];
    const userCompanyId = userCompany?.id;
    
    // Validate company ID exists
    if (!userCompanyId) {
      return { isValid: false, error: 'Invalid company access' };
    }
    
    // If target company ID is specified, validate access
    if (targetCompanyId && userCompanyId !== targetCompanyId) {
      return { isValid: false, error: 'Access denied: Company access mismatch' };
    }
    
    // Check user role for the specified application
    const userRole = userCompany?.applicationRoles?.['EASYWEBINAR'];
    
    // Define role hierarchy (higher number = more permissions)
    const roleHierarchy: { [key: string]: number } = {
      'USER': 1,
      'ADMIN': 2,
      'OWNER': 3
    };
    
    const requiredRoleLevel = roleHierarchy[requiredRole] || 0;
    const userRoleLevel = roleHierarchy[userRole] || 0;
    
    if (userRoleLevel < requiredRoleLevel) {
      return { 
        isValid: false, 
        error: `Access denied: Insufficient permissions. Required: ${requiredRole}, Current: ${userRole || 'NONE'}` 
      };
    }
    
    return { isValid: true, companyId: userCompanyId };
  } catch (error) {
    console.error('Error validating user permissions:', error);
    return { isValid: false, error: 'Error validating permissions' };
  }
};

/**
 * Security utility to validate company ID format and prevent injection attacks
 * @param companyId - The company ID to validate
 * @returns Object with validation result and error message if invalid
 */
export const validateCompanyId = (companyId: string): { isValid: boolean; error?: string } => {
  if (!companyId) {
    return { isValid: false, error: 'Company ID is required' };
  }
  
  if (typeof companyId !== 'string') {
    return { isValid: false, error: 'Company ID must be a string' };
  }
  
  const trimmedId = companyId.trim();
  
  if (trimmedId === '') {
    return { isValid: false, error: 'Company ID cannot be empty' };
  }
  
  // Check for potential injection patterns
  const dangerousPatterns = [
    /[<>'"&]/g,  // HTML/XML injection
    /[;|&$`]/g,  // Command injection
    /\.\./g,     // Path traversal
    /\/\//g,     // URL manipulation
    /\s+/g       // Whitespace injection
  ];
  
  for (const pattern of dangerousPatterns) {
    if (pattern.test(trimmedId)) {
      return { isValid: false, error: 'Invalid company ID format' };
    }
  }
  
  // Check length (reasonable limit)
  if (trimmedId.length > 100) {
    return { isValid: false, error: 'Company ID is too long' };
  }
  
  // Allow only alphanumeric characters, hyphens, and underscores
  const validIdPattern = /^[a-zA-Z0-9_-]+$/;
  if (!validIdPattern.test(trimmedId)) {
    return { isValid: false, error: 'Company ID contains invalid characters' };
  }
  
  return { isValid: true };
};

/**
 * Security utility to sanitize text input and prevent hyperlink injection
 * @param input - The text input to sanitize
 * @returns Sanitized text with HTML tags and dangerous characters removed
 */
export const sanitizeTextInput = (input: string): string => {
  if (!input || typeof input !== 'string') {
    return '';
  }
  
  // Remove HTML tags completely
  let sanitized = input.replace(/<[^>]*>/g, '');
  
  // Remove dangerous characters that could be used for injection
  sanitized = sanitized.replace(/[<>'"&;|$`]/g, '');
  
  // Remove potential script injection patterns
  sanitized = sanitized.replace(/javascript:/gi, '');
  sanitized = sanitized.replace(/data:/gi, '');
  sanitized = sanitized.replace(/vbscript:/gi, '');
  
  // Remove potential URL patterns that could be malicious
  sanitized = sanitized.replace(/https?:\/\/[^\s]+/gi, '');
  sanitized = sanitized.replace(/ftp:\/\/[^\s]+/gi, '');
  
  // Trim whitespace
  sanitized = sanitized.trim();
  
  // Limit length to prevent buffer overflow attacks
  if (sanitized.length > 100) {
    sanitized = sanitized.substring(0, 100);
  }
  
  return sanitized;
};

/**
 * Security utility to validate and sanitize name fields specifically
 * @param name - The name to validate and sanitize
 * @param fieldName - The field name for error messages
 * @param isRequired - Whether the field is required (default: true)
 * @returns Object with validation result and sanitized name
 */
export const validateAndSanitizeName = (
  name: string, 
  fieldName: string = 'Name',
  isRequired: boolean = true
): { isValid: boolean; error?: string; sanitizedValue?: string } => {
  // If field is not required and name is empty, return valid with empty string
  if (!isRequired && (!name || name.trim().length === 0)) {
    return { isValid: true, sanitizedValue: '' };
  }
  
  if (!name) {
    return { isValid: false, error: `${fieldName} is required` };
  }
  
  if (typeof name !== 'string') {
    return { isValid: false, error: `Invalid ${fieldName} format` };
  }
  
  const trimmedName = name.trim();
  
  if (trimmedName.length === 0) {
    return { isValid: false, error: `${fieldName} cannot be empty` };
  }
  
  // Check for HTML tags and hyperlinks
  const htmlTagPattern = /<[^>]*>/g;
  const hyperlinkPattern = /<a\s+[^>]*href\s*=\s*["'][^"']*["'][^>]*>/gi;
  const scriptPattern = /<script\b[^<]*(?:(?!<\/script>)<[^<]*)*<\/script>/gi;
  
  if (htmlTagPattern.test(trimmedName)) {
    return { isValid: false, error: `${fieldName} contains invalid HTML tags` };
  }
  
  if (hyperlinkPattern.test(trimmedName)) {
    return { isValid: false, error: `${fieldName} contains hyperlinks which are not allowed` };
  }
  
  if (scriptPattern.test(trimmedName)) {
    return { isValid: false, error: `${fieldName} contains script tags which are not allowed` };
  }
  
  // Check for dangerous characters
  const dangerousChars = /[<>'"&;|$`]/g;
  if (dangerousChars.test(trimmedName)) {
    return { isValid: false, error: `${fieldName} contains invalid characters` };
  }
  
  // Check for potential injection patterns
  const injectionPatterns = [
    /javascript:/gi,
    /data:/gi,
    /vbscript:/gi,
    /https?:\/\/[^\s]+/gi,
    /ftp:\/\/[^\s]+/gi
  ];
  
  for (const pattern of injectionPatterns) {
    if (pattern.test(trimmedName)) {
      return { isValid: false, error: `${fieldName} contains potentially malicious content` };
    }
  }
  
  // Check length
  if (trimmedName.length > 50) {
    return { isValid: false, error: `${fieldName} is too long (maximum 50 characters)` };
  }
  
  // Sanitize the name
  const sanitizedValue = sanitizeTextInput(trimmedName);
  
  return { isValid: true, sanitizedValue };
};
